import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Avatar, MasterDetailShell, PaneSkeleton, PaneEmpty, RailSearch, RailTimeline, RailRecents, SlidePane, StatusPill, hl, useToast, writeWithToast, useUnsavedGuard, useReportDirty } from '../common';
import { shortDate } from '../../lib/dates';
import { api, ApiError } from '../../lib/api';
import { revalidateBooking } from '../bookings/revalidateBooking';
import { useOwners } from './useOwners';
import { BookingForm } from '../bookings/BookingForm';
import { FilterControl, Icon, KeepOpenProvider } from '../common';
import { STATUS_PICK_OPTIONS, statusShown } from '../bookings/constants';
import { useDecide } from '../bookings/useDecide';
import { HouseholdCard, RequestCard, cardState } from '../bookings/RequestCard';
import { MoneyCard, MoneyCardNa, moneySender } from '../bookings/MoneyCard';
import { messageTo, senderFirst } from '../bookings/messageTo';
import { accessMessage, accessNextStay } from '../access/accessMessage';
import { VaccinationsCard, VaccinationsCardNa, cardSender, householdNextStay } from '../bookings/VaccinationsCard';
import { barDogFlags, householdBarFlags, type BookingFlag } from '../bookings/flags';
import type { BookingStatus } from '../bookings/types';
import { OwnerForm } from './OwnerForm';
import type { OwnerCreateDefaults, OwnerFormDog } from '../viewings/convert';
import { selectCarriedNote } from '../bookings/flags';
import { nightsBetween, nightsLabel, statusIsDirty } from '../bookings/domain';
import type { Booking, BookingDetail, Owner } from '../bookings/types';
import type { CardProof, OwnerTimelineEntry, Viewing } from '@bbbk/schema';
import { isClosedStatus, isLockedStatus, type AccessPerson } from '@bbbk/schema';
import { BookingNode, mergeTimeline, timelineDate, timelineKey, ViewingNode, type TimelineItem } from './ownerTimeline';
import { ViewingPane } from '../viewings/ViewingModal';
import { AccessColumnForNewHousehold, AccessPanel, type AccessLend } from '../access/AccessPanel';
import { SelfServiceSkeleton } from '../bookings/SelfServiceSkeleton';
import { AccessColumnSkeleton } from '../access/AccessColumnSkeleton';
import { landsOnOwner, rereadPlan } from './reread';


// owner-bookings timeline row (GET /owners/:id/bookings) — the wire shape is
// OwnerTimelineEntry, single-sourced in @bbbk/schema. The web loosens notes +
// createdAt to optional so the OPTIMISTIC seed node (built from a list-row
// Booking before the context fetch, where those two may be absent) type-checks;
// they feed the booking-flags note carry-forward (selectCarriedNote). Every
// other field single-sources from schema.
type OwnerBooking = Omit<OwnerTimelineEntry, 'notes' | 'createdAt'> & {
  notes?: string | null;
  createdAt?: string | null;
};
// Owner with the archived flag the context bundle carries (so OwnerForm needn't refetch).
type OwnerCtx = Owner & { archived?: boolean };

type Mode = 'booking' | 'ownerEdit' | 'new' | 'viewing';

// One entry on the modal's own back-stack: enough to put the pane back where it
// was, by id rather than by held object, so a stale owner or booking cannot be
// restored over a fresher one.
interface PaneState { mode: Mode; bookingId: string | null; ownerId: string | null; creating: boolean; viewingId: string | null }

// Map a list-row booking/owner into the instant seed: the rail card + timeline
// node + header render from this before the context fetch returns.
function seedFromBooking(b?: Booking | null): { owner: OwnerCtx; node: OwnerBooking } | null {
  if (!b) return null;
  return {
    owner: {
      id: b.ownerId, doNotMix: b.doNotMix, contacts: b.ownerContacts,
      dogs: b.dogs.map((d) => ({ id: d.id, ownerId: b.ownerId, name: d.name, breed: d.breed, size: d.size, vaccinesDue: d.vaccinesDue })),
    },
    node: { id: b.id, checkIn: b.checkIn, checkOut: b.checkOut, status: b.status, paymentStatus: b.paymentStatus ?? 'unpaid', total: b.total ?? null, dogs: b.dogs.map((d) => d.name).join(', '), notes: b.notes ?? null, createdAt: b.createdAt ?? null },
  };
}

import type { RecordFocus } from '../search/RecordModalProvider';

export interface OwnerBookingModalProps {
  bookingId?: string | null;
  /** The self-service card a notification bubble opens unfolded, on its dog's row (Freddi, 2026-10-14). */
  focus?: RecordFocus | null;
  ownerId?: string | null;
  // The clicked list row — seeds the rail + header so the modal paints instantly
  // while the detail hydrates from the context bundle.
  seed?: Booking | null;
  ownerSeed?: Owner | null;
  initialMode?: Mode;
  startNew?: boolean;
  createOwner?: boolean;
  // CONVERTING A VIEWING (book-them-in). `createDefaults` opens the household
  // form already carrying what the viewing knew; `onOwnerCreated` fires the
  // moment the household exists — which is when the visit is marked Visited —
  // and `onBookingCreated` when the booking does, which is when the link is
  // written. Two moments, because the user may legitimately stop between them
  // and AC5b defines what that leaves behind.
  createDefaults?: OwnerCreateDefaults;
  onHouseholdCreated?: (ownerId: string, dogs: OwnerFormDog[]) => void;
  onBookingCreated?: (bookingId: string) => void;
  // OPEN ON A VIEWING (Sir, 2026-09-02). The viewing form is a pane of THIS
  // modal — beside the same search, household card and history a booking has —
  // so a viewing and its booking are one press apart in either direction, on
  // every page that mounts the modal.
  viewing?: Viewing;
  today: string;
  onClose: () => void;
  onChanged: () => void;
  // Passed straight through to the shell — present only when this modal was
  // opened from somewhere that can be returned to (see RecordModalNav).
  onBack?: () => void;
  backLabel?: string;
}

interface BookingContext { booking: BookingDetail; owner: OwnerCtx; timeline: OwnerBooking[]; viewings?: Viewing[] }
/** `cards` — the household's dogs' vaccination cards, for the Vaccinations card on the household pane (2026-10-13). */
interface OwnerContext { owner: OwnerCtx; timeline: OwnerBooking[]; viewings?: Viewing[]; cards?: CardProof[] }

// Unified owner↔booking modal — the prototype's centered master-detail card.
// Opens INSTANTLY from the list-row seed (rail + header), then hydrates the
// detail pane from a single context bundle (one round-trip, no client waterfall).
export function OwnerBookingModal({ bookingId, focus = null, ownerId, seed, ownerSeed, initialMode, startNew, createOwner, createDefaults, onHouseholdCreated, onBookingCreated, viewing, today, onClose, onChanged, onBack, backLabel }: OwnerBookingModalProps) {
  const seeded = seedFromBooking(seed);
  const initialOwner: OwnerCtx | null = seeded?.owner ?? (ownerSeed ?? null);
  const [owner, setOwner] = useState<OwnerCtx | null>(initialOwner);
  const [bookings, setBookings] = useState<OwnerBooking[]>(seeded ? [seeded.node] : []);
  // The viewings this household came from — its history before its first stay.
  const [viewings, setViewings] = useState<Viewing[]>([]);
  const [sel, setSel] = useState<BookingDetail | null>(null);
  const [householdCards, setHouseholdCards] = useState<CardProof[]>([]);
  // THE UNSAVED-CHANGES GUARD on this modal's OWN navigation — another
  // household from the rail, a stay from the timeline, New booking, Edit
  // owner, a step back. The shell guards the ways OUT (Escape, close, Cancel)
  // itself; these are the ways ACROSS, which only this component knows.
  const { guard } = useUnsavedGuard();
  // The Saved mark beside the household for a few seconds after a keep-open
  // save (G14) — the prototype's saved-flag.
  const [savedFlash, setSavedFlash] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [mode, setMode] = useState<Mode>(viewing ? 'viewing' : (initialMode ?? 'booking'));
  /* THE BAR OWNS WHAT THE BAR DRAWS (Freddi, 2026-09-21: "they should all share
     the top bar"). Status is a control and the flags are a readout, both of the
     open booking — so they live here, beside the household they sit next to,
     rather than in the form that happens to save them. */
  const [status, setStatus] = useState<BookingStatus>('unconfirmed');
  /* THE DECISION ON THE BOOKING (accept-or-decline AC1, AC4, AC5): the
     request card heads the Self-service column. After a decision the record is
     re-read, so the card and the bar both move to what the booking now is. */
  // THE LIVE SELECTION, not the one captured when the decision went out: she
  // can move to another booking or household before the re-read lands, and
  // the stale closure would have swapped the first booking back in beside the
  // wrong household (PR #251 review).
  const selRef = useRef<BookingDetail | null>(null);
  selRef.current = sel;
  // The household on screen NOW, for the same reason (PR #255 review, #56).
  const ownerRef = useRef<OwnerCtx | null>(owner);
  ownerRef.current = owner;
  // `remount: false` for a decision: only the status moved, so the form keeps its place and simply
  // unlocks — a remount re-ran the pane's fade and read as a flash (Freddi, 2026-10-11).
  const rereadSel = (id: string, remount = true): Promise<void> => {
    const read = api<BookingDetail>(`/bookings/${id}`, { cache: 'reload' })
      .then((b) => { if (selRef.current?.id === id) { setSel(b.data); setStatus(b.data.status); if (remount) setSaveGen((g) => g + 1); } })
      .catch(() => undefined);
    // Only the household still open, and only while its booking is still the
    // selection: a write that finished after she moved on re-read the OLD
    // household over the new one (PR #255 review, #56).
    const plan = rereadPlan({ selectedId: selRef.current?.id ?? null, ownerId: ownerRef.current?.id ?? null }, id);
    const household = plan.ownerId ? loadBookings(plan.ownerId, true) : Promise.resolve();
    onChanged();
    return Promise.all([read, household]).then(() => undefined);
  };
  // The flip happens on the server's yes, before the re-read: the card, the bar and the Money card move together.
  // ACCEPTING, THEN A SKELETON, THEN THE CARD AS IT NOW IS (Freddi, 2026-10-14) — replacing the flip on
  // the server's yes (2026-10-11): the card holds a skeleton while the booking re-reads, and the new
  // state arrives from the record itself.
  const [decideSettling, setDecideSettling] = useState<string | null>(null);
  const onDecided = (id: string) => {
    setDecideSettling(id);
    void rereadSel(id, false).finally(() => setDecideSettling((cur) => (cur === id ? null : cur)));
  };
  const { busy: deciding, verdict: decidingVerdict, accept, decline } = useDecide(onDecided);
  /* THE SCREENSHOT'S THREE WRITES (deposit-proof AC10, AC11, AC20): Accept
     with the amount she confirmed, Not right, and her own upload — one POST
     each, one toast, the booking re-read so the Money card and the ledger
     move together. The server's own sentence is the error where it has one
     (already answered, nothing due, a screenshot already waiting). */
  const [moneyBusy, setMoneyBusy] = useState(false);
  const [moneySettling, setMoneySettling] = useState(false);
  const moneyWrite = (write: () => Promise<unknown>, success: string, fallback: string) => {
    const id = selRef.current?.id;
    setMoneyBusy(true);
    void writeWithToast(toast, {
      write,
      success,
      error: (e) => (e instanceof ApiError && e.message ? e.message : fallback),
      // While the booking re-reads, the card shows the decision as a skeleton (Freddi, 2026-10-14).
      onSaved: () => {
        setMoneyBusy(false);
        setMoneySettling(true);
        const owner = ownerRef.current?.id;
        const reread = id ? rereadSel(id) : owner ? loadBookings(owner, true) : Promise.resolve();
        void Promise.resolve(reread).finally(() => setMoneySettling(false));
      },
      onError: (e) => {
        setMoneyBusy(false);
        // Answered elsewhere, or a second upload beat this one: re-read, so the card stops offering what is gone.
        if (e instanceof ApiError && (e.code === 'already_decided' || e.code === 'proof_pending' || e.code === 'card_superseded') && id) rereadSel(id);
      },
    });
  };
  const money = {
    accept: (proofId: string, amount: number) => moneyWrite(() => api(`/proofs/${proofId}/accept`, { method: 'POST', body: { amount } }), 'Payment recorded', 'Could not record the payment.'),
    notRight: (proofId: string) => moneyWrite(() => api(`/proofs/${proofId}/reject`, { method: 'POST', body: {} }), 'Marked not right — the message is on the card', 'Could not mark the screenshot.'),
    remove: (proofId: string) => moneyWrite(() => api(`/proofs/${proofId}`, { method: 'DELETE' }), 'Screenshot removed', 'Could not remove the screenshot.'),
    upload: (file: File, kind?: 'deposit' | 'remainder') => {
      const form = new FormData();
      form.set('file', file);
      if (kind) form.set('kind', kind);
      const id = selRef.current?.id;
      if (!id) return;
      moneyWrite(() => api(`/bookings/${id}/proofs`, { method: 'POST', form }), 'Screenshot added', 'Could not add the screenshot.');
    },
  };
  /* THE CARD'S FOUR WRITES (vaccination-cards AC4–AC8): Accept with the date
     she confirmed, Not accepted, the ×, and her own card for a dog — the money's
     own pattern, one POST each, one toast, the booking re-read so the card and
     the flags move together. */
  // The household pane judges its dogs' cards against the next stay still to come, or today with none.
  const nextStayIn = householdNextStay(bookings, today);
  // Its own status, so a request still waiting is never told "See you on" (audit, 2026-10-15).
  const nextStayStatus = bookings.find((b) => b.checkIn === nextStayIn && !isClosedStatus(b.status as never))?.status ?? 'confirmed';
  // THE HOUSEHOLD'S NEXT STAY IN FULL, FOR APP ACCESS (audit, 2026-10-15): the household pane said only
  // "booked in" for a stay still owing its deposit — the timeline row carries no money. Read the stay
  // itself, again whenever the timeline is re-read after a write; the open stay when it is that one.
  const accessNext = accessNextStay(bookings, today);
  const [nextRead, setNextRead] = useState<BookingDetail | null>(null);
  useEffect(() => {
    if (!accessNext || sel?.id === accessNext.id) return;
    let gone = false;
    api<BookingDetail>(`/bookings/${accessNext.id}`, { cache: 'reload', quiet: true })
      .then((r) => { if (!gone) setNextRead(r.data); })
      .catch(() => undefined);
    return () => { gone = true; };
  }, [accessNext?.id, bookings, sel?.id]);
  const nextDetail = !accessNext ? null : sel?.id === accessNext.id ? sel : nextRead?.id === accessNext.id ? nextRead : null;
  // WHOEVER SENT IT (PR #260 review; Freddi, 2026-10-13): a card or screenshot names the household's
  // person with that sign-in, not whoever sits first on the household — found through App access's
  // people, as the recipient is, so a relay sign-in still has a name (PR #275 review).
  const nameIn = (access: AccessLend) => (email: string) => senderFirst({ people: access.people, contacts: owner?.contacts ?? [], email });
  // WHO EACH CARD'S WORDS ARE FOR, AND THE LINK LINE (messages-one-shape): the person who sent what
  // the card is about, else the household's newest sign-in — from App access's own read — and the
  // link App access would give them, made only when Patricia taps WhatsApp.
  // APP ACCESS SAYS THE NEXT STEP (Freddi, 2026-10-14): the message beside a person's link is the
  // deposit when the stay needs it, else the stay and the link — never a vaccination card, which is
  // the Vaccinations card's own message (Freddi, 2026-10-15).
  const accessDraft = (person: AccessPerson, url: string, registered: boolean) => {
    const contact = owner?.contacts.find((c) => c.id === person.contactId);
    const first = contact?.firstName || person.name.split(' ')[0] || 'there';
    // The status on screen, saved or not (PR #275 review), as the Money and Vaccinations cards read it.
    const open = sel && owner && sel.ownerId === owner.id ? (mode === 'booking' ? { ...sel, status: status as BookingDetail['status'] } : sel) : null;
    // The household's next stay still to come, for when the open one is over (Freddi, 2026-10-15) or none is
    // open: read in full once it has loaded, named from its row until then.
    const stay = accessNext ? { checkIn: accessNext.checkIn, checkOut: accessNext.checkOut, dogs: accessNext.dogs } : null;
    return accessMessage({ first, url, registered, booking: open, next: nextDetail, stay, today });
  };
  const toFor = (access: AccessLend, sender: string | null) =>
    messageTo({ people: access.people, contacts: owner?.contacts ?? [], sender, origin: window.location.origin, issueLink: access.issueLink });
  // A CARD IS THE DOG'S (2026-10-13): after any card write the household's cards are read again
  // too, so the household pane's card moves as the booking's does.
  // On a refusal too (PR #260 review): answered on another screen, or a newer card in, and the household
  // pane — which has no booking to re-read — would go on offering what is gone.
  const cardWrite = (write: () => Promise<unknown>, success: string, fallback: string) =>
    moneyWrite(async () => {
      // A refusal re-reads here; a success re-reads in moneyWrite, under the skeleton.
      try { return await write(); } catch (e) { if (ownerRef.current?.id) void loadBookings(ownerRef.current.id, true); throw e; }
    }, success, fallback);
  const cards = {
    accept: (proofId: string, validUntil: string) => cardWrite(() => api(`/cards/${proofId}/accept`, { method: 'POST', body: { validUntil } }), 'Card accepted', 'Could not accept the card.'),
    reject: (proofId: string) => cardWrite(() => api(`/cards/${proofId}/reject`, { method: 'POST', body: {} }), 'Marked not accepted — the message is on the card', 'Could not mark the card.'),
    // The × on the card (Freddi, 2026-10-13): the card goes, and its area is the upload zone again.
    remove: (proofId: string) => cardWrite(() => api(`/cards/${proofId}`, { method: 'DELETE' }), 'Card removed', 'Could not remove the card.'),
    upload: (dogId: string, file: File) => {
      const form = new FormData();
      form.set('file', file);
      cardWrite(() => api(`/dogs/${encodeURIComponent(dogId)}/card`, { method: 'POST', form }), 'Card added', 'Could not add the card.');
    },
  };
  // Null until the open form's first report — a static paint reads the saved
  // rows for the dogs' chips rather than a flash of nothing (see barDogFlags).
  const [live, setLiveFlags] = useState<BookingFlag[] | null>(null);
  const liveFlags = live ?? [];

  /* RE-SEEDED ON THE STAY, NOT ON THE MOUNT. `BookingForm` is keyed on the
     booking id so it remounts per stay and its own fields reseed for free; this
     state sits ABOVE that key, so moving from one stay to another would
     otherwise leave the previous one's status sitting in the bar. */
  useEffect(() => {
    setStatus(mode === 'new' ? 'unconfirmed' : (sel?.status ?? 'unconfirmed'));
  }, [sel?.id, sel?.status, mode]);
  // THE STATUS IS THIS COMPONENT'S TO GUARD — it owns the bar's state and the
  // booking it was set from, so the comparison is between two things that live
  // here. Declared AFTER the reseed above, so the re-render it causes reports
  // the settled answer.
  useReportDirty('booking-status', statusIsDirty(mode, sel?.status, status), 'this booking');

  /* WATCHING THE STATUS, NOT JUST THE ID. A seeded modal paints from the row
     before it hydrates, and hydration replaces `sel` with the same booking
     carrying its real status — same id, so an effect keyed on the id alone
     never ran again and the bar sat on `unconfirmed` for a confirmed stay.
     Her own pick does not re-trigger it: `sel.status` only moves when the
     SERVER's answer moves. */

  /* NO CLEAR HERE, AND THAT IS THE POINT. A clear effect ran after the form had
     already reported its flags — React runs a child's effects before its
     parent's — so on the seeded path the report was wiped the moment it
     arrived and never re-fired, because its signature had not changed. The
     form is keyed on the booking id, so moving to another stay remounts it and
     it reports that stay's flags (or an empty list) for itself. The child owns
     the value; the parent only holds it. */
  // The viewing on the pane, in 'viewing' mode.
  const [viewingSel, setViewingSel] = useState<Viewing | null>(viewing ?? null);
  // EVERY VIEWING THIS MODAL HAS SHOWN, by id — so Back can put the frame's own
  // viewing back on the pane. `viewingSel` alone was left at whatever
  // `openViewing` last set: open one household's viewing, search to a second
  // and open its viewing, press Back twice, and the pane showed the first
  // household with the second household's viewing on it (review, PR #174).
  const seenViewingsRef = useRef<Map<string, Viewing>>(new Map(viewing ? [[viewing.id, viewing]] : []));
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(startNew ?? false);
  const [wantNew, setWantNew] = useState(!!startNew);
  const [creating, setCreating] = useState(!!createOwner);
  // True while switching households — holds the skeleton across the context fetch
  // AND the follow-on booking load, so the empty "No stay selected" pane never
  // flashes in the gap.
  const [switching, setSwitching] = useState(false);
  const toast = useToast();
  const { owners: results } = useOwners(focused || query.trim().length > 0, query.trim() || null);
  // Recent owners for the standing rail list when no owner is picked yet (new
  // owner / new booking) — the analogue of the bookings timeline once one is.
  const { owners: recentOwners } = useOwners(!owner, null);
  // WHERE BACK GOES. Every forward move inside the modal — a stay picked from
  // the rail, the owner editor, a new booking, a change of household — pushes
  // where it was leaving FROM. Back pops one; with the stack empty it falls
  // through to whatever opened the modal (search), and with neither it is the
  // disabled arrow. The moves the modal makes for itself do NOT push: hydrating
  // on open, and following a household switch to that owner's next stay, are
  // arrivals rather than steps.
  const [history, setHistory] = useState<PaneState[]>([]);
  // Which way the pane's next move is going, so SlidePane can bring the content
  // in from the side it ought to. Every navigation sets it, so it is never read
  // stale — a push sets 'forward', a pop sets 'back'.
  const [navDir, setNavDir] = useState<'forward' | 'back'>('forward');
  // KEPT OPEN, REHYDRATED (review on #239, three findings with one cause). A
  // form that stayed mounted after its save went on holding what it had opened
  // with — a new dog with no id yet, a payment slot still an append, a create
  // still in `new` — and the next Save wrote it all again. So the pane is
  // REMOUNTED from the fresh record after every kept-open save: this counter
  // joins the pane keys below, and the record it mounts from is re-read first.
  // What the eye had — the scroll — is put back by the effect under it.
  const [saveGen, setSaveGen] = useState(0);
  const keptScroll = useRef<[string, number][]>([]);
  useLayoutEffect(() => {
    if (saveGen === 0) return;
    const root = document.querySelector('.modal-foot .keep-open')?.closest('.obx') ?? document;
    for (const [cls, top] of keptScroll.current) {
      const el = root.querySelector<HTMLElement>('.' + cls);
      if (el) el.scrollTop = top;
    }
    keptScroll.current = [];
  }, [saveGen]);
  const switchGenRef = useRef(0);
  // `selIdRef` lived here — the on-screen booking id, held in a ref so
  // `refreshBooking`'s async guard could tell a stale in-flight payment refresh
  // from the current selection. Nothing reads it now that the payments panel and
  // its non-closing refresh are gone, and dead state that describes a race which
  // no longer exists is worse than no state at all. Restore it with
  // `refreshBooking` when the panel is remounted.

  const loadBookings = useCallback(async (id: string, onlyIfOpen = false) => {
    // The context bundle rather than /bookings alone: the household's viewings
    // travel with it, and a household just created from a viewing has one.
    try {
      const r = await api<OwnerContext>(`/owners/${id}/context`, { cache: 'reload' });
      // A re-read lands only on the household still open (PR #255 review, #56).
      if (onlyIfOpen && !landsOnOwner(ownerRef.current?.id ?? null, id)) return;
      // THE HOUSEHOLD TOO, not only its timeline. After a keep-open save of the
      // household the modal went on holding the household it had opened with
      // — Pebble still at `mix` — and the booking form's own pre-save check,
      // reading that stale roster, refused the accept the server would have
      // allowed (Freddi, 2026-10-08). The context bundle carries the fresh
      // household; this is the one place it lands.
      if (r.data?.owner) setOwner(r.data.owner);
      setBookings(r.data?.timeline ?? []);
      setViewings(r.data?.viewings ?? []);
      setHouseholdCards(r.data?.cards ?? []);
    } catch { /* empty */ }
  }, []);

  // Single-round-trip hydrate: the context bundle returns booking + owner +
  // timeline together (owner + timeline run concurrently server-side).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (bookingId) {
          const r = await api<BookingContext>(`/bookings/${bookingId}/context`); if (cancelled) return;
          setOwner(r.data.owner); setSel(r.data.booking); setBookings(r.data.timeline); setViewings(r.data.viewings ?? []); setMode('booking'); setHydrated(true);
        } else if (ownerId) {
          const r = await api<OwnerContext>(`/owners/${ownerId}/context`); if (cancelled) return;
          setOwner(r.data.owner); setBookings(r.data.timeline); setViewings(r.data.viewings ?? []); setHouseholdCards(r.data.cards ?? []); setMode(initialMode ?? 'ownerEdit'); setHydrated(true);
        } else if (viewing) {
          // The household this viewing became, or — for one that carries a
          // booking and not yet the household — that booking's context. A
          // viewing that became nothing has no household to load, and the
          // rail shows the viewing alone.
          if (viewing.ownerId) {
            const r = await api<OwnerContext>(`/owners/${viewing.ownerId}/context`); if (cancelled) return;
            setOwner(r.data.owner); setBookings(r.data.timeline); setViewings(r.data.viewings ?? []); setHouseholdCards(r.data.cards ?? []);
          } else if (viewing.bookingId) {
            const r = await api<BookingContext>(`/bookings/${viewing.bookingId}/context`); if (cancelled) return;
            setOwner(r.data.owner); setBookings(r.data.timeline); setViewings(r.data.viewings ?? []);
          }
          setHydrated(true);
        }
      } catch (e) { if (!(e instanceof ApiError && e.status === 401)) toast.error('Could not open that record.'); }
    })();
    return () => { cancelled = true; };
  }, [bookingId, ownerId, initialMode, toast, viewing]);

  // gen guards an awaited open against a newer household switch landing first.
  const openBooking = async (id: string, gen?: number) => {
    try {
      const b = await api<BookingDetail>(`/bookings/${id}`);
      if (gen !== undefined && gen !== switchGenRef.current) return; // superseded switch
      setSel(b.data); setMode('booking');
    }
    catch (e) { if (!(e instanceof ApiError && e.status === 401)) toast.error('Could not open that booking.'); }
  };
  const switchOwner = async (o: Owner) => {
    const gen = ++switchGenRef.current;
    const keepNew = wantNew || mode === 'new'; // composing a new booking → keep it for the new owner
    // Skeleton from the first click until the destination pane is ready (booking
    // loaded, or a new-booking form). hydrated resets so a stale owner can't
    // satisfy the pane mid-switch.
    setSwitching(true);
    setOwner(o); setQuery(''); setFocused(false); setSel(null); setCreating(false); setHydrated(false);
    // One context call (owner + timeline concurrent) instead of a refetch chain.
    const r = await api<OwnerContext>(`/owners/${o.id}/context`).catch(() => null);
    if (gen !== switchGenRef.current) return; // a newer owner switch superseded this response
    if (r?.data) { setOwner(r.data.owner); setHydrated(true); setViewings(r.data.viewings ?? []); setHouseholdCards(r.data.cards ?? []); }
    const list = r?.data?.timeline ?? []; setBookings(list);
    if (keepNew) { setMode('new'); setWantNew(false); setSwitching(false); return; }
    // A declined request is closed too: never the stay to open (PR #251 review).
    const next = list.filter((b) => b.checkIn >= today && !isClosedStatus(b.status)).sort((a, b) => a.checkIn.localeCompare(b.checkIn))[0] ?? list[0];
    if (next) await openBooking(next.id, gen); else setMode('new');
    if (gen === switchGenRef.current) setSwitching(false);
  };
  // A non-closing refresh for an in-place payment write used to live here
  // (`refreshBooking`), feeding BookingForm's payments panel. The panel is no
  // longer mounted, so nothing inside the modal writes without saving and there
  // is nothing left to refresh in place. It comes back with the panel.

  // Saving an existing booking/owner closes the modal (create flows close too).
  // The booking context embeds the owner + its dogs and is cacheable, so an owner/
  // dog edit here would otherwise reopen stale — revalidate the open booking's
  // reads before closing so the reopen reflects the change (write-revalidation).
  const afterSave = async (opts?: { stay?: boolean; createdId?: string }) => {
    const bid = sel?.id ?? bookingId ?? null;
    const oid = owner?.id ?? null;
    if (bid) await revalidateBooking(bid);
    // The inherited note (booking-flags carriedNote) is DERIVED across the owner's
    // bookings — editing one booking's note can change what its siblings inherit,
    // and new bookings read it from the owner timeline. Bust those caches too, or a
    // deleted note keeps showing on the next booking's modal. Fire-and-forget: they
    // only need to be fresh by the next open, not before this one closes.
    for (const b of bookings) if (b.id !== bid) void revalidateBooking(b.id);
    if (oid) {
      void api(`/owners/${oid}/context`, { cache: 'reload' }).catch(() => undefined);
      void api(`/owners/${oid}/bookings`, { cache: 'reload' }).catch(() => undefined);
    }
    onChanged();
    if (opts?.stay) {
      // KEEP OPEN (G14): saved, and nothing moves — same pane, same scroll. The
      // rail's timeline re-reads so a changed date or status shows there too,
      // and the Saved mark says what just happened. The pane itself remounts
      // from the record as the server now holds it (see saveGen): a kept-open
      // create lands on the booking it made; an edit re-reads its booking.
      const root = document.querySelector('.modal-foot .keep-open')?.closest('.obx');
      keptScroll.current = root
        ? Array.from(root.querySelectorAll<HTMLElement>('*')).filter((el) => el.scrollTop > 0 && el.className)
            .map((el) => [String(el.className).split(' ')[0]!, el.scrollTop])
        : [];
      try {
        if (opts.createdId) {
          const b = await api<BookingDetail>(`/bookings/${opts.createdId}`, { cache: 'reload' });
          setSel(b.data); setMode('booking');
        } else if (bid && mode === 'booking') {
          const b = await api<BookingDetail>(`/bookings/${bid}`, { cache: 'reload' });
          setSel(b.data);
        }
      } catch { /* the stale pane is still a saved pane; the next open re-reads */ }
      if (oid) await loadBookings(oid);
      setSaveGen((g) => g + 1);
      setSavedFlash(true);
      window.setTimeout(() => setSavedFlash(false), 2600);
      return;
    }
    onClose();
  };
  const here = (): PaneState => ({ mode, bookingId: sel?.id ?? null, ownerId: owner?.id ?? null, creating, viewingId: viewingSel?.id ?? null });
  // Only a move that actually GOES somewhere is a step. Pushing unconditionally
  // stacked an entry identical to the pane already on screen, so the next Back
  // restored what was already there and looked like a dead button — the rail's
  // booking rows guarded against this from the start and the rest did not.
  const push = (to: Partial<PaneState> = {}) => {
    const from = here();
    const same =
      (to.mode ?? from.mode) === from.mode &&
      (to.bookingId ?? from.bookingId) === from.bookingId &&
      (to.ownerId ?? from.ownerId) === from.ownerId &&
      (to.creating ?? from.creating) === from.creating &&
      (to.viewingId ?? from.viewingId) === from.viewingId;
    // Set BEFORE the no-op guard. navDir is one piece of state that persists
    // until the next navigation sets it, so a forward move that happens not to
    // stack an entry must still say which way it is going — otherwise it
    // inherits 'back' from the last Back and the pane slides in from the wrong
    // side. Direction describes the movement, not the bookkeeping.
    setNavDir('forward');
    if (same) return;
    setHistory((h) => [...h, from]);
  };

  // The household pane draws its dogs' cards; opened from a booking, they were never read (PR #260 review).
  const newBooking = () => guard(() => { push({ mode: 'new', bookingId: null }); setSel(null); setMode('new'); if (owner) void loadBookings(owner.id, true); });
  // A viewing picked from the rail's history opens on the pane, as a stay does.
  const openViewing = (v: Viewing) => guard(() => { push({ mode: 'viewing', viewingId: v.id }); seenViewingsRef.current.set(v.id, v); setViewingSel(v); setMode('viewing'); });
  const editOwner = () => guard(() => { push({ mode: 'ownerEdit' }); setMode('ownerEdit'); });
  const startCreate = () => guard(() => { push({ mode: 'ownerEdit', ownerId: null, creating: true }); setCreating(true); setOwner(null); setSel(null); setQuery(''); setFocused(false); setMode('ownerEdit'); });

  // Put the pane back. Restoring by id means re-fetching when the household
  // changed under us — the alternative, holding the old owner object, restores
  // a record that may have been edited since.
  const restore = async (p: PaneState) => {
    setCreating(p.creating);
    if (p.mode === 'viewing') {
      // THE FRAME'S OWN VIEWING, not whichever was opened last.
      setViewingSel((p.viewingId && seenViewingsRef.current.get(p.viewingId)) || null);
      setMode('viewing');
      if (p.ownerId && owner?.id === p.ownerId) return;
    }
    // A frame with no owner is a state, not a missing value: New booking and
    // New owner both begin there. Restoring only when the id was truthy left
    // Back unable to return to that flow — New booking stayed on the household
    // just chosen, and New owner became an edit of it.
    if (!p.ownerId) {
      // Bump the generation here too. This branch is synchronous, so it looked
      // like it had no race to lose — but an ALREADY IN-FLIGHT switch or restore
      // checks the generation before it writes, and without a bump it still
      // passes that check and resurrects the household this Back just cleared.
      ++switchGenRef.current;
      setOwner(null);
      setBookings([]);
      setSel(null);
      setQuery('');
      setFocused(false);
      setMode(p.mode);
      return;
    }
    if (p.ownerId !== (owner?.id ?? null)) {
      const gen = ++switchGenRef.current;
      setSwitching(true);
      // The selection goes FIRST. Clearing the owner without clearing the
      // booking left the outgoing stay rendered against the incoming
      // household's owner and dogs — and saving that form would have PATCHed
      // the old booking with the new household's ownerId and dogIds.
      setSel(null);
      setHydrated(false);
      const r = await api<OwnerContext>(`/owners/${p.ownerId}/context`).catch(() => null);
      if (gen !== switchGenRef.current) return;
      // WITHOUT THE HOUSEHOLD THERE IS NO RESTORE. Carrying on into the booking
      // load with a null context left the restored stay rendered against the
      // household that was current BEFORE Back — and saving it would have
      // PATCHed that booking with the wrong ownerId and dogIds, which is the
      // same fault the ordering fix above was for, reached by a failed request
      // instead of a slow one.
      if (!r?.data) {
        setSwitching(false);
        toast.error('Could not go back to that household.');
        return;
      }
      setOwner(r.data.owner); setBookings(r.data.timeline); setHouseholdCards(r.data.cards ?? []); setHydrated(true);
      // The skeleton stays up for the booking request too. Dropping it between
      // the two showed a pane that was briefly neither household's.
      if (p.mode === 'booking' && p.bookingId) {
        await openBooking(p.bookingId, gen);
        if (gen === switchGenRef.current) setSwitching(false);
        return;
      }
      setSwitching(false);
      setMode(p.mode);
      return;
    }
    if (p.mode === 'booking' && p.bookingId) { await openBooking(p.bookingId); return; }
    if (p.mode !== 'booking') setSel(null);
    setMode(p.mode);
  };

  // The stack first, then whatever opened the modal, then nothing — and
  // `undefined` is what renders the arrow disabled.
  const inStack = history.length > 0;
  const goBack = inStack
    ? () => guard(() => {
        const p = history[history.length - 1]!;
        setNavDir('back');
        setHistory((h) => h.slice(0, -1));
        void restore(p);
      })
    : onBack;
  // After creating a new owner → load them and drop straight into a new booking.
  const onOwnerCreated = async (newId?: string, dogs: OwnerFormDog[] = []) => {
    onChanged();
    // THE HOUSEHOLD EXISTS. Told before anything else, and before the chain can
    // fail: this is the AC5b ending — household on file, viewing Visited, no
    // booking link — and it has to be recorded even if the user closes the
    // booking form that opens next.
    if (newId) onHouseholdCreated?.(newId, dogs);
    if (!newId) { onClose(); return; }
    const o = await api<OwnerCtx>(`/owners/${newId}`).catch(() => null);
    if (!o?.data) { onClose(); return; }
    setNavDir('forward'); // arriving somewhere new, not retracing anything
    setCreating(false); setWantNew(false); setOwner(o.data); setSel(null); setHydrated(true);
    // WHERE A FRESH HOUSEHOLD LANDS, and it depends on why it was created.
    //
    // Converting a VIEWING carries straight on into the booking — that is the
    // whole point of book-them-in, and the visit is only half-converted until
    // the stay exists.
    //
    // Creating one from the New menu now STAYS on the household (Freddi,
    // 2026-09-12). Its people have just come into existence, so App access has
    // something to say for the first time — each of them at *Not registered*,
    // with a link one press away. Dropping straight into a booking form hid
    // that behind a pane nobody asked for, on the one screen where issuing the
    // link is the next thing Patricia does.
    setMode(createDefaults ? 'new' : 'ownerEdit');
    void loadBookings(o.data.id);
  };

  // `closeRail` is supplied by the shell at the phone tier, where the rail is a
  // drawer over the pane: picking a stay has to shut it, or the booking you just
  // chose opens behind the list you chose it from.
  const renderNode = (b: OwnerBooking, closeRail?: () => void): ReactNode => (
    <BookingNode
      key={b.id}
      b={b}
      on={mode === 'booking' && sel?.id === b.id}
      onOpen={(id) => guard(() => {
        closeRail?.();
        push({ mode: 'booking', bookingId: id });
        void openBooking(id);
      })}
    />
  );

  // Takes `closeRail` for the same reason every other pick path does: at the
  // phone tier the rail is a drawer OVER the pane, so a choice made in it that
  // does not dismiss it hides the very thing it just opened.
  const currentCardFor = (closeRail?: () => void) => owner && (
    <button type="button" className={'curowner' + (mode === 'ownerEdit' ? ' active' : '')} onClick={() => { closeRail?.(); editOwner(); }} title="Edit owner record">
      <Avatar name={owner.contacts[0]?.name ?? '?'} id={owner.id} size={32} />
      <div style={{ minWidth: 0 }}>
        <div className="nm">{owner.contacts[0]?.name ?? '—'}{owner.doNotMix && <span className="dnm">⚠</span>}</div>
        <div className="mt">{owner.dogs.map((d) => d.name).join(', ')} · {bookings.filter((b) => !isClosedStatus(b.status)).length} stays</div>
      </div>
      <span className="pencil">✎</span>
    </button>
  );

  // The viewing on the pane is in the history even when it became nothing yet —
  // it is the one entry a household-less viewing has.
  // …and only while it is the pane's own: once the household changes beneath
  // it, a viewing from elsewhere is not this household's history.
  const railViewings = mode === 'viewing' && viewingSel && !viewings.some((x) => x.id === viewingSel.id) ? [...viewings, viewingSel] : viewings;
  const rail = (closeRail?: () => void) => (
    <>
      <RailSearch<Owner>
        placeholder="Search owners or dogs…"
        query={query} setQuery={setQuery} focused={focused} setFocused={setFocused}
        items={results} idOf={(o) => o.id} isCurrent={(o) => o.id === owner?.id}
        // The SEARCH box is the primary way to change household — Recent only
        // ever surfaces one already visited — so it has to push like Recent does,
        // or the back arrow is dead on the commonest journey through this rail.
        onPick={(o) => guard(() => { closeRail?.(); push({ ownerId: o.id }); void switchOwner(o); })} restLabel="Recent" addLabel="Add new owner"
        onAdd={() => { closeRail?.(); startCreate(); }} current={currentCardFor(closeRail)} noMatchText="No owner matches"
        showOnFocus={!!owner}
        renderRow={(o, q) => (
          <>
            <Avatar name={o.contacts[0]?.name ?? '?'} id={o.id} size={30} />
            <div><div className="nm">{hl(o.contacts[0]?.name ?? '—', q)}</div><div className="mt">{o.dogs.map((d) => d.name).join(', ')}{o.doNotMix && <> · <span className="dnm">⚠</span></>}</div></div>
            {o.id === owner?.id ? <span className="curtick">● current</span> : <div className="rt">{o.visits ?? 0} stays</div>}
          </>
        )}
      />
      {owner || (mode === 'viewing' && viewingSel) ? (
        <RailTimeline<TimelineItem<OwnerBooking>>
          headLabel="Bookings" items={mergeTimeline(bookings, railViewings)} dateOf={timelineDate}
          // The viewings sit in the history; the head counts the stays alone.
          count={bookings.length}
          activeKey={mode === 'viewing' ? 'v' + (viewingSel?.id ?? '') : mode === 'booking' ? sel?.id ?? null : null}
          renderNode={(i) =>
            i.kind === 'booking' ? (
              renderNode(i.b, closeRail)
            ) : (
              <ViewingNode
                key={timelineKey(i)}
                v={i.v}
                on={mode === 'viewing' && viewingSel?.id === i.v.id}
                onOpen={(v) => { closeRail?.(); openViewing(v); }}
              />
            )
          }
          // NO "+ NEW" WITHOUT A HOUSEHOLD. On a viewing that became nothing
          // yet there is nobody to book for, and the button led to an empty
          // pane asking for a household (Sir, 2026-09-02). Book on the desk is
          // the way in; once the household exists, + New books it as usual.
          onNew={owner ? () => { closeRail?.(); newBooking(); } : undefined}
          newActive={mode === 'new'}
          emptyText="No bookings yet. Hit + New to add the first."
        />
      ) : (
        <RailRecents<Owner>
          items={recentOwners.slice(0, 10)}
          idOf={(o) => o.id}
          isCurrent={() => false}
          onPick={(o) => guard(() => { closeRail?.(); push({ ownerId: o.id }); void switchOwner(o); })}
          renderRow={(o) => (
            <>
              <Avatar name={o.contacts[0]?.name ?? '?'} id={o.id} size={30} />
              <div className="rail-rowtext">
                <div className="nm">{o.contacts[0]?.name ?? '—'}{o.doNotMix && <span className="dnm">⚠</span>}</div>
                <div className="mt">{o.dogs.map((d) => d.name).join(', ')}</div>
              </div>
              <div className="rt">{o.visits ?? 0} stays</div>
            </>
          )}
        />
      )}
    </>
  );

  let pane: ReactNode;
  if (creating && !owner) pane = <OwnerForm key="new-owner" owner={null} createDefaults={createDefaults} onSaved={onOwnerCreated} onClose={onClose} />;
  else if (mode === 'viewing' && viewingSel)
    pane = (
      <ViewingPane
        key={'viewing-' + viewingSel.id}
        viewing={viewingSel}
        today={today}
        onClose={onClose}
        onSaved={onChanged}
      />
    );
  // OPENED BY ID WITH NO SEED (a search hit): the record is on its way, so the
  // pane is its skeleton — never *Start a booking*, which is for a modal opened
  // with nothing to load (Freddi, 2026-10-11: "it should not load any other
  // empty states then kick into the actual contents").
  else if (!owner && (bookingId || ownerId) && !hydrated) pane = <PaneSkeleton />;
  else if (!owner) pane = <PaneEmpty icon="paw" title="Start a booking" hint="Pick a household from the list, or search to find one — then add their stay." />;
  else if (switching) pane = <PaneSkeleton />;
  else if (mode === 'ownerEdit') pane = hydrated ? <OwnerForm key={'owner-' + owner.id + ':' + saveGen} owner={owner} archived={owner.archived} onSaved={(_id, _dogs, opts) => void afterSave(opts)} onClose={onClose} /> : <PaneSkeleton />;
  else if (mode === 'new') pane = <BookingForm key={'new-booking-' + owner.id} booking={null} owner={owner} carriedNote={selectCarriedNote(bookings)} today={today} onSaved={afterSave} onCreated={onBookingCreated} onClose={onClose} onEditOwner={editOwner} status={status} onStatusChange={setStatus} onFlagsChange={setLiveFlags} />;
  // Seeded-but-not-hydrated → skeleton (the rail + header already paint from seed).
  // Keyed on the booking id and the save generation (a kept-open save
  // remounts from the fresh record — see saveGen) and NOTHING ELSE. Widening the key to include the
  // ledger did re-seed the money fields, but it remounted the whole form — and
  // every other field (notes, dates, care flags, status, rate) is seeded from the
  // prop in a useState initialiser too, so an unsaved note would be discarded
  // whenever the ledger moved. BookingForm re-seeds JUST its money fields itself.
  else if (sel) pane = <BookingForm key={sel.id + ':' + saveGen} booking={sel} owner={owner} today={today} onSaved={afterSave} onClose={onClose} onEditOwner={editOwner} status={status} onStatusChange={setStatus} onFlagsChange={setLiveFlags} />;
  else if (!hydrated) pane = <PaneSkeleton />;
  else pane = <PaneEmpty icon="calendar" title="No stay selected" hint="Pick a stay from the timeline, or hit + New to add one." />;

  /* EACH WARNING BESIDE WHAT IT IS ABOUT (Freddi, 2026-09-21). The dogs carry
     their own — a vaccine short, medication, kept apart — and what is left is
     the household's, which sits by the name. The note is always the
     household's: it is about the stay, not any one dog in it. */
  const barStayStart = sel?.checkIn ?? today;
  // Against the stay's own rows, and the form's live ticks once it has spoken
  // (PR #228 review, codex 2026-10-06) — the chip sat beside the dog with
  // standing text rather than the dog ticked this stay.
  const dogFlags = barDogFlags(owner?.dogs ?? [], barStayStart, sel?.dogs, live);
  const shownPerDog = new Set([...dogFlags.values()].flat().map((f) => f.kind));
  const headFlags = householdBarFlags(liveFlags, shownPerDog);

  const modeLabel = creating && !owner ? 'new owner' : mode === 'viewing' ? 'viewing' : mode === 'ownerEdit' ? 'edit owner' : mode === 'new' ? 'new booking' : 'booking';

  return (
    // The keep-open tick is born with the modal and dies with it — off on
    // every open (keepOpen.tsx).
    <KeepOpenProvider>
    <MasterDetailShell
      title={owner?.contacts[0]?.name ?? (creating ? 'New owner' : mode === 'viewing' ? viewingSel?.contactName || 'Viewing' : 'Booking')}
      modeLabel={modeLabel}
      /* ONE HEADER (Freddi, 2026-09-19: "we basically moved all this out of the
         edit bookign form and up to the top bar of the modal").

         A booking pane used to name the household in this bar and then name it
         again in a header of its own beneath — the same words twice, and the
         identity in the lower one, where it read as the form's first field
         rather than as the modal's subject.

         The subject moves up. Only the booking pane gets this: an owner form or
         a viewing has no second identity to fold in, and their plain title is
         already the whole truth about what is open. */
      head={
        // The slot stays EMPTY when there is no subject to draw — the shell
        // falls back to its title and mode label — and gains the Saved mark
        // only beside a subject it already draws.
        (mode === 'booking' || mode === 'new') && owner ? (<>{(
          <span className="obx-subject">
            {/* The avatar keeps the affordance it had in the pane header: it is
                the way into the household record from a stay. */}
            <button type="button" className="obx-subject-av" onClick={editOwner} title="Edit owner" aria-label="Edit owner">
              {/* 40, WHICH IS THE HEIGHT OF WHAT IT LABELS (Freddi, 2026-09-21:
                  "the household avatar should be larger ... it looks
                  incomplete"). At 28 against a 41px two-line block it sat six
                  pixels inside the top and six inside the bottom — anchoring
                  neither the name nor the dogs, just hovering between them.
                  Flush with the block, it reads as the block's subject, which
                  is what a media object is for.

                  It is also exactly TWICE the dogs' 20: the household is the
                  parent and they are its members, and the ratio says so. 28
                  against 20 was 1.4 — neither matching nor clearly different,
                  which is the reading of a size nobody chose. */}
              {/* 28px, the Status control's height (Freddi, 2026-10-10: "too large … save space vertically"). */}
              <Avatar name={owner.contacts[0]?.name ?? '?'} id={owner.id} size={28} />
            </button>
            <span className="obx-subject-txt">
              {/* THE HOUSEHOLD IS THE TITLE (2026-09-19). *Edit Booking* named
                  the screen she had just chosen to open and spent the bar's
                  heaviest type doing it. The badge sits INSIDE the name: a
                  sibling lands in the column flex and draws as a full-width
                  bar on a line of its own. */}
              {/* THE NAME'S ROW: who, then the two things that qualify them —
                  whether they are new to us, and what needs attention before
                  this stay. Both are exceptions to an ordinary booking, so they
                  sit together and read at a glance beside the household they
                  belong to (Freddi, 2026-09-21). */}
              <span className="obx-line">
                <strong>{owner.contacts[0]?.name ?? '—'}</strong>
                {sel?.ownerVisits === 1 && <span className="bflag-new" title="First stay — new household">New</span>}
                {headFlags.length > 0 && (
                  <span className="obx-flags">
                    {headFlags.map((f, n) => (
                      <span key={`${f.kind}-${f.dogId ?? 'h'}-${n}`} className="obx-flag" title={f.dogName ? `${f.detail} — ${f.dogName}` : f.detail}>
                        <span className={'bflag tint-' + f.tint}><Icon name={f.icon} size={12} /></span>
                        <b>{f.label}</b>
                      </span>
                    ))}
                  </span>
                )}
              </span>
              {/* THE DOGS, WITH THEIR OWN FACES (Freddi, 2026-09-21). A comma
                  never told two dogs apart; the avatar's tone does it before
                  the name is read, and it is the same face the rest of the app
                  gives them — the booking rows, the care rows, the household.

                  THE STAYS COUNTER IS GONE. "1 stays" was bad English and a
                  number nobody acts on: the rail directly beneath lists every
                  stay this household has had, counted, dated and priced. A
                  total above a list of the things it totals is furniture. */}
              <span className="mut obx-dogs">
                {owner.dogs.length === 0 ? (
                  'No dogs on file'
                ) : (
                  owner.dogs.map((d) => (
                    <span key={d.id} className="obx-dog">
                      <Avatar name={d.name} id={d.id} kind="dog" size={20} />
                      <span>{d.breed ? `${d.name} (${d.breed})` : d.name}</span>
                      {/* ICON ONLY, because the dog beside it is the label. On
                          the name row a chip needs its word — there is nothing
                          up there to say who it is about. Here the word would
                          be the third time the row says the same thing. */}
                      {(dogFlags.get(d.id) ?? []).map((f, n) => (
                        <span key={`${f.kind}-${n}`} className={'bflag tint-' + f.tint} title={`${f.label} — ${f.detail}`}>
                          <Icon name={f.icon} size={12} />
                        </span>
                      ))}
                    </span>
                  ))
                )}
              </span>
            </span>
            {/* THE BOOKING'S OWN FACTS, ON THE SAME ROW (Freddi, 2026-09-21:
                "they should all share the top bar in its previous size").

                They were a second strip under this one, which grew the header
                into the form. The bar is one row again: the household on the
                left says WHOSE this is, and this group on the right says what
                state it is in — the two halves of the one question she asks
                when the modal opens, read in the order she asks them.

                Right-aligned rather than flowing after the name, because the
                name's length varies and a group that started in a different
                place on every household would read as a list rather than a
                position. */}
            <span className="obx-state">
              {/* SHUT WHILE IT IS A REQUEST (AC2), and on a declined one (AC6):
                  the same Status control as every booking, greyed and inert
                  (Freddi, 2026-10-10: "it should appear the same as previous
                  but should be disabled"). The decision is the request card's;
                  the hint says so on hover. */}
              {(() => {
                const shut = mode === 'booking' && !!sel && isLockedStatus(sel.status);
                return (
                  <FilterControl
                    label="Status"
                    mode="single"
                    clearable={false}
                    options={shut ? statusShown(sel!.status) : STATUS_PICK_OPTIONS}
                    selected={[shut ? sel!.status : status]}
                    onChange={(keys) => { if (keys[0]) setStatus(keys[0] as BookingStatus); }}
                    disabled={shut}
                    disabledHint={sel?.status === 'declined' ? 'This request was declined' : 'Accept or decline the request in Self-service'}
                  />
                );
              })()}
            </span>
          </span>
        )}{savedFlash && <span className="saved-flag" role="status">Saved</span>}</>) : undefined
      }
      onClose={onClose}
      /* THE SAME MODAL WHETHER OR NOT A VIEWING OPENED IT. A first draft gave
         the conversion route its own shape — no rail, two columns, a taller
         card — so the whole household form fitted without a scroll. Sir asked
         for the ordinary New owner modal with the known values filled in
         (2026-09-02), and that is what `createDefaults` now means: values, and
         nothing about the chrome. */
      rail={({ closeRail }: { closeRail: () => void }) => rail(closeRail)}
      railLabel="Bookings"
      onBack={goBack}
      // Only the OUTERMOST back leaves the modal. A step taken inside it changes
      // the pane and the sheet stays put.
      backExits={!inStack && !!onBack}
      // But how the modal ARRIVED does not change as the stack grows: reached
      // from a search result it pushes in from the right for its whole life.
      pushed={!!onBack}
      backLabel={inStack ? 'Back' : backLabel}
      /* APP ACCESS SITS BESIDE THE HOUSEHOLD FORM AND NOWHERE ELSE
         (household-app-access, flows 1.1/1.2/1.5). It is a column about the
         PEOPLE on this household, so it appears when the household is what is
         on the pane — not beside a stay, a viewing or a half-typed new
         household that has no contacts saved yet to issue links to. */
      /* APP ACCESS IS ALWAYS ON THIS MODAL, with no exceptions (Freddi,
         2026-09-12). It has an empty state and a full one, so there is no pane
         it needs to hide behind: a household being created shows the line that
         says where its people will appear, and a household that exists shows
         where each of them stands — whether the pane beside it is the household
         form, a stay, a new booking or a viewing. A column that came and went
         with the pane made the card jump and hid the one control Patricia
         reaches for straight after saving somebody.

         It does NOT wait on `hydrated`: the ownerId arrives with the list-row
         seed, which is everything this column needs to start its own read. */
      aside={owner && mode === 'booking' && !sel ? (
        /* NOTHING OF THE COLUMN BEFORE THE BOOKING IT BELONGS TO (Freddi,
           2026-10-11: "the app access part of the self service card loads
           first and then the rest loads"). The container is the request card,
           and the request card is the booking's — so until the booking is here
           the column is the cards' own frame with nothing in it: the shut
           request card's head, and the Money card's head when the list row
           already says this is an app booking. The structure does not change
           when the booking lands; only the words arrive. */
        <SelfServiceSkeleton />
      ) : owner ? (
        /* ONE CONTAINER (Freddi, 2026-10-10: "app access card should live
           within self service container"). On an app booking the request card
           is the column, App access inside it; on a desk booking App access
           stands alone under the column's own heading. */
        <AccessPanel ownerId={owner.id} draft={accessDraft} frame={
          /* ONE COLUMN ON BOTH PANES (Freddi, 2026-10-11): the stay that is open on
             this household carries into its household pane; with none, the same
             frame reads for the household and Money is greyed. */
          sel && sel.ownerId === owner.id && cardState(sel) !== null ? (rows, access) => (
          <>
            <RequestCard booking={sel} status={mode === 'booking' ? status : sel.status} owner={owner} today={today} busy={deciding === sel.id || decideSettling === sel.id} busyVerdict={deciding === sel.id ? decidingVerdict : null} settling={decideSettling === sel.id}
              onAccept={() => accept(sel.id)} onDecline={() => decline(sel.id)}>{rows}</RequestCard>
            {/* THE MONEY CARD, the request card's sibling (deposit-proof AC9,
                AC9a; Freddi, 2026-10-11) — on an app booking from unconfirmed
                on, reading the status as the bar now has it. */}
              <MoneyCard status={mode === 'booking' ? status : sel.status} key={sel.id} booking={sel} defaultOpen={focus?.card === 'money' && sel.id === bookingId} {...toFor(access, moneySender(sel))} nameOf={nameIn(access)}
                today={today} busy={moneyBusy} settling={moneySettling}
                onAccept={money.accept} onNotRight={money.notRight} onUpload={money.upload} onRemove={money.remove} />
              {/* THE VACCINATIONS CARD, standalone under Money and built from its parts
                  (vaccination-cards AC13; Freddi, 2026-10-13). */}
              <VaccinationsCard key={`vax-${sel.id}`} booking={mode === 'booking' ? { ...sel, status } : sel}
                defaultOpen={focus?.card === 'vax' && sel.id === bookingId} focusDog={focus?.card === 'vax' && sel.id === bookingId ? focus.dogId : null}
                {...toFor(access, cardSender(sel, today))} nameOf={nameIn(access)}
                today={today} busy={moneyBusy} settling={moneySettling}
                onAccept={cards.accept} onReject={cards.reject} onRemove={cards.remove} onUpload={cards.upload} />
          </>
        ) : (rows, access) => (
          <>
            <HouseholdCard>{rows}</HouseholdCard>
            <MoneyCardNa why="No stay open" />
            {/* LIVE ON THE HOUSEHOLD (Freddi, 2026-10-13: "i uploaded a vacination card as a customer
                and its not appearing"): a card is the dog's, not the stay's — every dog on the
                household, judged against the next stay, or today with none. */}
            {owner.dogs.length > 0
              ? <VaccinationsCard key={`vax-${owner.id}`} booking={{ dogs: owner.dogs, cards: householdCards, checkIn: nextStayIn, status: nextStayStatus }}
                  defaultOpen={focus?.card === 'vax' && !bookingId} focusDog={focus?.card === 'vax' && !bookingId ? focus.dogId : null}
                  {...toFor(access, cardSender({ dogs: owner.dogs, cards: householdCards, checkIn: nextStayIn, status: nextStayStatus }, today))} nameOf={nameIn(access)}
                  today={today} busy={moneyBusy} settling={moneySettling}
                  onAccept={cards.accept} onReject={cards.reject} onRemove={cards.remove} onUpload={cards.upload} />
              : <VaccinationsCardNa why="No dogs on this household" />}
          </>
        )} />
      ) : (bookingId || ownerId) && !hydrated ? (
        /* The same, for the column: a booking's is the cards' frame, a
           household's is Self-service with its rows — never the new-household
           line while a record that exists is loading. */
        <SelfServiceSkeleton />
      ) : <AccessColumnForNewHousehold />}
    >
      {/* Moving from one booking to another is forward navigation, so it pushes
          in from the right — the same gesture as arriving at a record from a
          search result. The key is what is being shown, not the mode: switching
          household or opening a different stay both count as moving on. */}
      <SlidePane
        slideKey={mode === 'booking' ? (sel?.id ?? null) : mode + ':' + (owner?.id ?? '')}
        dir={navDir}
      >
        {pane}
      </SlidePane>
    </MasterDetailShell>
    </KeepOpenProvider>
  );
}
